import { useContentfulLiveUpdates } from "@contentful/live-preview/react";
import classNames from "classnames";
import { Entry } from "contentful";
import dynamic from "next/dynamic";
import React from "react";

import BreakpointAwareAsset from "~/components/common/breakpoint-aware-asset";
import EditorialEntry from "~/components/editorials/editorial-entry";
import Editorials from "~/types/editorials";
import { assertEditorialType, isActiveEntry } from "~/utils/editorial-utils";

import styles from "./editorial-element-plp.module.scss";

const EditorialCardPLP = dynamic(() => import("./editorial-card-plp"));
const ServicesCarouselPLP = dynamic(() => import("./services-carousel-plp"));
const EditorialProductListing = dynamic(
  () => import("../../editorials/editorial-product-listing/editorial-product-listing")
);

type Props = {
  editorialElement: Entry<unknown>;
};

export default function EditorialElementPLP({ editorialElement }: Props) {
  assertEditorialType<Editorials.GridElement>(editorialElement, "gridElement");
  const { component, numberColumnsMobile, numberColumnsDesktop } = editorialElement.fields;
  const liveEntry = useContentfulLiveUpdates(component);

  if (!isActiveEntry(component)) {
    return null;
  }

  switch (liveEntry?.sys.contentType?.sys.id) {
    //special cases
    case "card":
      return <EditorialCardPLP entry={liveEntry} numberColumnsDesktop={numberColumnsDesktop} />;
    case "servicesCarousel":
      return (
        <ServicesCarouselPLP
          entry={liveEntry}
          numberColumnsMobile={numberColumnsMobile}
          numberColumnsDesktop={numberColumnsDesktop}
        />
      );
    case "editorialProductListing":
      return (
        <div className="EditorialElementPLPContainer">
          <EditorialProductListing entry={liveEntry} gridElement={{ numberColumnsMobile, numberColumnsDesktop }} />
        </div>
      );
    case "breakpointAwareAsset":
      assertEditorialType<Editorials.ContentAwareAsset>(liveEntry, "breakpointAwareAsset");
      return (
        <div
          className={classNames(
            styles.media,
            numberColumnsMobile === 2 ? styles.maxContentMobile : undefined,
            numberColumnsDesktop === 4 ? styles.maxContentDesktop : undefined
          )}
        >
          <BreakpointAwareAsset entry={component} />
        </div>
      );

    default: {
      //standard cases
      return (
        <div className="EditorialElementPLPContainer">
          <EditorialEntry entry={component} />
        </div>
      );
    }
  }
}
